import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from 'components/Modal/Modal';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ModalParams } from 'hooks/useModal';
import { Controller, useForm } from 'react-hook-form';
import { ConfigRequirements, ConfigRequirementsSchema } from './schema';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import Checkbox from 'components/Checkbox/Checkbox';
import clsx from 'clsx';
import Loader from 'components/Loader/Loader';
import {
  carrierOptions,
  downloadOptions,
  ipBlockOptions,
  ipTypeOptions,
  termOptions,
} from 'screens/AccessExpressV3/utils';
import { useEffect } from 'react';
import { usePermissions } from 'hooks/usePermissions';
import { TokenUserPermissions } from 'api/users/schemas/Users';
import { MultiValue } from 'react-select';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';

export interface ConfigRequirementsModalProps extends ModalParams {
  isLoading: boolean;
  onConfirmation: (data: ConfigRequirements) => void;
  defaultValues: Partial<ConfigRequirements> | undefined;
}

const ConfigRequirementsModal = ({
  isOpen,
  close,
  isLoading,
  onConfirmation,
  defaultValues,
}: ConfigRequirementsModalProps) => {
  const { hasPermission } = usePermissions({
    permission: TokenUserPermissions.USER_MANAGEMENT,
    forGraniteUser: true,
  });
  const cart = useCartContext();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ConfigRequirements>({
    resolver: zodResolver(ConfigRequirementsSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) reset({ ...defaultValues });
  }, [defaultValues, reset]);

  const [ipType, preferredCarriers, excludeCarriers] = watch([
    'ip_type',
    'preferred_carriers',
    'exclude_carriers',
  ]);

  const onSubmit = (data: ConfigRequirements) => {
    onConfirmation(data);
  };

  const onError = (err: unknown) => console.log(err);

  return (
    <Modal
      isVisible={isOpen}
      close={close}
      className="max-h-[90vh] w-full max-w-[624px] overflow-y-auto"
    >
      <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6">
        <h1 className="text-[28px] leading-9 text-content-base-default">
          Define your service needs{' '}
        </h1>
        <p className="font-semibold leading-[22px] text-content-base-subdued">
          Set your preferences to jumpstart a targeted serviceability search.
        </p>
      </div>
      <div className="flex flex-col gap-12 p-8">
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="grid grid-cols-2 gap-6"
        >
          {/* may be needed later */}
          {/* <div className="col-span-1 flex flex-row items-center gap-0">
            <GraniteInput
              label="Min download"
              placeholder="E.g., 50"
              variant="blue"
              innerInputClassName="!w-full !box-border !rounded-r-none"
              {...register(`min_download`)}
              error={errors.min_download?.message}
            />
            <Controller
              name="min_unit"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <GraniteSelect
                  {...field}
                  options={unitOptions}
                  onChange={onChange}
                  label="unit"
                  value={value}
                  className="basis-1/2 self-baseline [&>label]:invisible"
                  error={errors.min_unit?.message}
                  variant="blue"
                  classNames={{
                    control: () => '!rounded-l-none',
                  }}
                />
              )}
            />
          </div> */}

          <Controller
            name="download"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <GraniteSelect
                {...field}
                options={downloadOptions}
                className="w-full"
                onChange={onChange}
                label="Download"
                value={value}
                error={errors.download?.message}
                variant="blue"
              />
            )}
          />

          <Controller
            name="term"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <GraniteSelect
                {...field}
                options={termOptions}
                className="w-full"
                onChange={onChange}
                label="Term length"
                value={value}
                error={errors.term?.message}
                variant="blue"
              />
            )}
          />

          <Controller
            name="preferred_carriers"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const handleChange = (selected: MultiValue<OptionType>) => {
                const updatedExclude = excludeCarriers?.filter(
                  (item) => !selected.some((inc) => inc.value === item.value),
                );
                setValue('exclude_carriers', updatedExclude);
                onChange(selected);
              };

              const filteredOptions = carrierOptions.filter(
                (opt) => !excludeCarriers?.some((ex) => ex.value === opt.value),
              );
              return (
                <GraniteSelect
                  {...field}
                  options={filteredOptions}
                  className="w-full"
                  onChange={handleChange}
                  label="Include carriers (optional)"
                  value={value}
                  error={errors.preferred_carriers?.message}
                  variant="blue"
                  isMulti
                />
              );
            }}
          />
          <Controller
            name="exclude_carriers"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const handleChange = (selected: MultiValue<OptionType>) => {
                const updatedInclude = preferredCarriers?.filter(
                  (item) => !selected.some((ex) => ex.value === item.value),
                );
                setValue('preferred_carriers', updatedInclude);
                onChange(selected);
              };

              const filteredOptions = carrierOptions.filter(
                (opt) =>
                  !preferredCarriers?.some((inc) => inc.value === opt.value),
              );

              return (
                <GraniteSelect
                  {...field}
                  options={filteredOptions}
                  className="w-full"
                  onChange={handleChange}
                  label="Exclude carriers (optional)"
                  value={value}
                  error={errors.exclude_carriers?.message}
                  variant="blue"
                  isMulti
                />
              );
            }}
          />
          {cart.selectedAccessProductCategory !== 'All' && (
            <>
              <Controller
                name="ip_type"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <GraniteSelect
                    {...field}
                    options={ipTypeOptions}
                    className="w-full"
                    onChange={(e) => {
                      setValue('ip_block', undefined);
                      onChange(e);
                    }}
                    label="IP type"
                    value={value}
                    error={errors.ip_type?.message}
                    variant="blue"
                    isDisabled={cart.selectedAccessProductCategory === 'DIA'}
                  />
                )}
              />
              <Controller
                name="ip_block"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <GraniteSelect
                    {...field}
                    options={ipBlockOptions}
                    className="w-full"
                    onChange={onChange}
                    label="IP block (optional)"
                    value={value}
                    error={errors.ip_block?.message}
                    variant="blue"
                    isDisabled={ipType?.value === 'dynamic'}
                  />
                )}
              />
            </>
          )}
          {hasPermission && (
            <div className="col-span-full">
              <h3 className="mb-2 text-base font-bold text-content-base-subdued">
                Save settings for future searches (optional)
              </h3>
              <Controller
                name="save_default"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Checkbox
                    {...field}
                    label="Save as company default"
                    className="col-span-full"
                    checked={value}
                    onChange={onChange}
                    variant="blue"
                  />
                )}
              />
            </div>
          )}
          <div className="col-span-full mt-6 flex gap-5">
            <GraniteButton size="large" variant="secondary" onClick={close}>
              Cancel
            </GraniteButton>
            <GraniteButton
              size="large"
              variant="primary"
              type="submit"
              className={clsx(isLoading && 'loading-button')}
              disabled={isLoading}
            >
              View services
              {isLoading && <Loader animationClassname="!w-3 !h-3" />}
            </GraniteButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ConfigRequirementsModal;
